import { getToken, isTokenValid } from '../auth/tokenManager';
import { get as apiGet } from '../../redux/api';
import { buildSteps } from '../../components/onboarding/influencer/stepConfig';
import { getBrandQuickSteps } from '../../components/onboarding/brand/brandOnboardingStepConfig';
import {
  INFLUENCER_PLATFORM_HANDLE_FIELDS,
  sanitizeInfluencerHandle,
  derivePlatformTypesFromHandles,
} from '../../constants/influencerOptions';
import { canonicalizeCategoryList } from '../../constants/marketplaceCategories';
import { getInfluencerOfferings } from '../influencer/influencerOfferings';
import {
  abandonWizardCheckFlight,
  readRecentWizardCheck,
  readSettledWizardCheck,
  releaseWizardCheckFlight,
  rememberWizardCheck,
  rememberWizardCheckForGeneration,
  resetRecentWizardCheck,
  storeWizardCheckFlight,
  takeWizardCheckFlight,
  WIZARD_SETTLED_STORAGE_KEY,
} from './wizardCheckSession';

export {
  WIZARD_SETTLED_STORAGE_KEY,
  rememberWizardCheck,
  readRecentWizardCheck,
  readSettledWizardCheck,
  resetRecentWizardCheck,
};

export const PROFILE_SETUP_NAV_EVENT = 'millet:profile-setup-navigate';

export const WIZARD_ONBOARDING_PATHS = {
  influencer: '/influencer-onboarding',
  brand: '/brand-onboarding',
};

const WIZARD_GATE_ALLOWED_PREFIXES = [
  '/influencer-onboarding',
  '/brand-onboarding',
  '/onboarding',
  '/login',
  '/logout',
  '/sign-up',
  '/signup',
  '/mfa-verification',
  '/forgot-password',
  '/reset-password',
  '/terms-and-conditions',
  '/legal',
  '/privacy-policy',
  '/cookie-notice',
  '/contact-us',
  '/contact',
  '/faq',
  '/blog',
  '/pricing',
];

function readTrimmed(...values) {
  const match = values.find((value) => typeof value === 'string' && value.trim());
  return match ? match.trim() : '';
}

function readList(...values) {
  const match = values.find((value) => Array.isArray(value));
  return match || [];
}

function deriveInfluencerPlatformTypes(profile, handles) {
  if (Array.isArray(profile.platformTypes) && profile.platformTypes.length > 0) {
    return profile.platformTypes;
  }

  const offeringPlatforms = [...new Set(
    getInfluencerOfferings(profile)
      .map((offering) => offering.platform)
      .filter(Boolean),
  )];
  if (offeringPlatforms.length > 0) return offeringPlatforms;

  return INFLUENCER_PLATFORM_HANDLE_FIELDS
    .filter(({ key }) => handles[key])
    .map(({ platform }) => platform);
}

const INFLUENCER_TASK_DESTINATIONS = {
  quick_onboarding: { pathname: '/influencer-onboarding' },
  creator_name: { pathname: '/dashboard/profile', edit: 'personal', focus: 'name' },
  social_handles: { pathname: '/dashboard/profile', edit: 'personal', focus: 'handles' },
  about: { pathname: '/dashboard/profile', edit: 'personal', focus: 'about' },
  shipping_address: { pathname: '/dashboard/profile', edit: 'personal', focus: 'shipping' },
  content_categories: { pathname: '/dashboard/profile', edit: 'content' },
  content_deliverables: { pathname: '/dashboard/profile', edit: 'content' },
  profile_photo: { pathname: '/dashboard/profile', edit: 'images', focus: 'photo' },
  portfolio: { pathname: '/dashboard/profile', edit: 'images', focus: 'portfolio' },
  rates: { pathname: '/dashboard/profile', edit: 'rates', focus: 'feeModels' },
  verify_tiktok: { pathname: '/dashboard/profile', edit: 'personal', focus: 'handles' },
  live_shopping_clip: { pathname: '/dashboard/profile', edit: 'images', focus: 'liveShopping' },
  past_brands: { pathname: '/dashboard/profile', edit: 'personal', focus: 'pastBrands' },
};

const BRAND_TASK_DESTINATIONS = {
  quick_onboarding: { pathname: '/brand-onboarding' },
  brand_name: { pathname: '/dashboard/profile', edit: 'company', focus: 'name' },
  brand_logo: { pathname: '/dashboard/profile', edit: 'logo' },
  brand_description: { pathname: '/dashboard/profile', edit: 'company', focus: 'description' },
  creator_categories: { pathname: '/dashboard/profile', edit: 'marketing' },
  brand_link: { pathname: '/dashboard/profile', edit: 'company', focus: 'links' },
  first_campaign: { pathname: '/dashboard/campaigns/create' },
  annual_revenue: { pathname: '/dashboard/profile', edit: 'company', focus: 'revenue' },
  brand_banner: { pathname: '/dashboard/profile', edit: 'images' },
};

export const INFLUENCER_SETUP_FOCUS_IDS = {
  name: 'inf-full-name',
  handles: 'inf-tiktok',
  about: 'inf-about',
  pastBrands: 'inf-past-brands',
  shipping: 'inf-shipping-heading',
  photo: 'profile-photo-setup',
  portfolio: 'influencer-portfolio-setup',
  liveShopping: 'influencer-live-shopping-setup',
  feeModels: 'inf-fee-models',
};

export const BRAND_SETUP_FOCUS_IDS = {
  name: 'brand-company-name',
  description: 'brand-company-description',
  links: 'brand-website',
  revenue: 'brand-annual-revenue',
};

function getTaskDestination(task) {
  return INFLUENCER_TASK_DESTINATIONS[task?.id] || BRAND_TASK_DESTINATIONS[task?.id] || null;
}

function hrefFromDestination(destination) {
  if (!destination?.pathname) return '/dashboard/profile';
  const params = new URLSearchParams();
  if (destination.edit) params.set('edit', destination.edit);
  if (destination.focus) params.set('focus', destination.focus);
  const query = params.toString();
  return query ? `${destination.pathname}?${query}` : destination.pathname;
}

export function getOnboardingTaskHref(task) {
  const destination = getTaskDestination(task);
  if (destination) return hrefFromDestination(destination);
  return task?.href || task?.hrefOrAction || '/dashboard/profile';
}

export function appendReturnTo(href, returnTo) {
  if (!returnTo) return href;
  const [path, query = ''] = href.split('?');
  const params = new URLSearchParams(query);
  params.set('returnTo', returnTo);
  const nextQuery = params.toString();
  return nextQuery ? `${path}?${nextQuery}` : path;
}

function destinationFromHref(href, task) {
  const mapped = getTaskDestination(task);
  try {
    const url = new URL(href, 'https://millet.local');
    return {
      pathname: url.pathname,
      edit: url.searchParams.get('edit') || mapped?.edit || null,
      focus: url.searchParams.get('focus') || mapped?.focus || null,
    };
  } catch {
    return {
      pathname: mapped?.pathname || '/dashboard/profile',
      edit: mapped?.edit || null,
      focus: mapped?.focus || null,
    };
  }
}

export function navigateToOnboardingTask(navigate, location, task, returnTo = '') {
  const href = appendReturnTo(getOnboardingTaskHref(task), returnTo);
  const destination = destinationFromHref(href, task);

  if (location?.pathname === destination.pathname) {
    window.dispatchEvent(new CustomEvent(PROFILE_SETUP_NAV_EVENT, {
      detail: destination,
    }));
  }

  navigate(href);
}

export function scrollToSetupFocus(focus, focusIds) {
  if (!focus) return;
  const elementId = focusIds[focus];
  if (!elementId) return;
  const node = document.getElementById(elementId);
  if (!node) return;
  node.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

export function getWizardOnboardingPath(userType) {
  return WIZARD_ONBOARDING_PATHS[userType] || null;
}

export function isWizardOnboardingPath(pathname = '') {
  const path = String(pathname).split('?')[0];
  return path === WIZARD_ONBOARDING_PATHS.brand
    || path === WIZARD_ONBOARDING_PATHS.influencer
    || path === '/onboarding';
}

export function isAllowedWithoutWizard(pathname = '') {
  return WIZARD_GATE_ALLOWED_PREFIXES.some((route) => (
    pathname === route || pathname.startsWith(`${route}/`)
  ));
}

export function mapInfluencerProfileToWizardForm(profile = {}) {
  const tiktokHandle = sanitizeInfluencerHandle(
    readTrimmed(profile.tiktokHandle, profile.tiktok_handle),
  );
  const instagramHandle = sanitizeInfluencerHandle(
    readTrimmed(profile.instagramHandle, profile.instagram_handle),
  );
  const whatnotHandle = sanitizeInfluencerHandle(
    readTrimmed(profile.whatnotHandle, profile.whatnot_handle),
  );
  const handles = { tiktokHandle, instagramHandle, whatnotHandle };

  return {
    fullName: readTrimmed(profile.fullName, profile.full_name),
    about: readTrimmed(profile.about),
    profilePictureUrl: readTrimmed(profile.profilePictureUrl, profile.profile_picture_url),
    tiktokHandle,
    instagramHandle,
    whatnotHandle,
    platformTypes: deriveInfluencerPlatformTypes(profile, handles),
    contentCategories: canonicalizeCategoryList(
      readList(profile.contentCategories, profile.content_categories),
    ),
  };
}

export function mapBrandProfileToWizardForm(profile = {}) {
  return {
    fullName: readTrimmed(profile.fullName, profile.full_name),
    companyName: readTrimmed(profile.companyName, profile.company_name),
    companyDescription: readTrimmed(profile.companyDescription, profile.company_description),
    brandLogoUrl: readTrimmed(profile.brandLogoUrl, profile.brand_logo_url),
    influencerCategories: canonicalizeCategoryList(
      readList(profile.influencerCategories, profile.influencer_categories),
    ),
    tiktok: readTrimmed(profile.tiktok),
    instagram: readTrimmed(profile.instagram),
    whatnot: readTrimmed(profile.whatnot),
    annualRevenue: readTrimmed(profile.annualRevenue, profile.annual_revenue),
  };
}

function isRequiredWizardStepSatisfied(step, formData) {
  if (!step.required) return true;
  // Settings never required a word minimum, so a short existing bio still counts as done.
  return !step.validate?.(formData, { skipWordMinimum: true });
}

export function isInfluencerWizardComplete(profile) {
  if (!profile) return false;
  const formData = mapInfluencerProfileToWizardForm(profile);
  return buildSteps().every((step) => isRequiredWizardStepSatisfied(step, formData));
}

export function isBrandWizardComplete(profile) {
  if (!profile) return false;
  const formData = mapBrandProfileToWizardForm(profile);
  return getBrandQuickSteps().every((step) => isRequiredWizardStepSatisfied(step, formData));
}

export function isWizardRequiredComplete(userType, profile) {
  if (userType === 'influencer') return isInfluencerWizardComplete(profile);
  if (userType === 'brand') return isBrandWizardComplete(profile);
  return true;
}

function optionalStepHasProgress(step, formData) {
  if (step.inputType === 'contentProfile') {
    return derivePlatformTypesFromHandles(formData).length > 0;
  }
  return false;
}

export function getFirstIncompleteWizardStepIndex(steps, formData) {
  const firstInvalid = steps.findIndex((step) => step.validate && step.validate(formData));
  if (firstInvalid === -1) {
    const firstOptional = steps.findIndex((step) => step.required === false);
    return firstOptional === -1 ? 0 : firstOptional;
  }
  const emptyOptionalBefore = steps.findIndex((step, index) => (
    index < firstInvalid && step.required === false && !optionalStepHasProgress(step, formData)
  ));
  if (emptyOptionalBefore !== -1) return emptyOptionalBefore;
  return firstInvalid;
}

async function loadWizardRequiredComplete(userType, token, generation) {
  if (!token || !isTokenValid(token)) {
    // Auth failed. Keep a known completion, and do not record this as incomplete.
    return readSettledWizardCheck(userType)?.complete === true;
  }

  const endpoint = userType === 'influencer'
    ? '/api/influencer/profile'
    : '/api/brand/profile';

  try {
    const response = await apiGet(endpoint);
    const complete = isWizardRequiredComplete(userType, response.data);
    rememberWizardCheckForGeneration(userType, complete, generation);
    return complete;
  } catch (err) {
    if (err.response?.status === 404) {
      rememberWizardCheckForGeneration(userType, false, generation);
      return false;
    }
    throw err;
  }
}

export async function fetchWizardRequiredComplete(userType) {
  if (userType !== 'brand' && userType !== 'influencer') return true;

  const token = getToken();
  const flight = takeWizardCheckFlight(userType, token);
  if (flight.cachedComplete != null) return flight.cachedComplete;
  if (flight.promise) return flight.promise;

  const pending = {};
  pending.promise = loadWizardRequiredComplete(userType, token, flight.generation).finally(() => {
    releaseWizardCheckFlight(pending.promise);
  });
  storeWizardCheckFlight(flight.flightKey, pending.promise);
  return pending.promise;
}

export function retryWizardRequiredComplete(userType) {
  abandonWizardCheckFlight();
  return fetchWizardRequiredComplete(userType);
}
